import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { trackEvent, currentLocationTag, toEventSlug } from '@/lib/analytics';
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

type ToastType = 'success' | 'error' | 'warning' | 'info';

/**
 * An optional link on a toast (task 865). One use today: every delete says the
 * resource can be restored, and "View Archive" is what makes that a promise the
 * User can act on rather than a place they have to go and find.
 *
 * **A plain `<a href>`, not a router `Link`.** `ToastProvider` wraps the
 * `<Router>` in `App.tsx`, so the container renders OUTSIDE the router context
 * and a `Link` here would throw. The cost is a full page load on a link that is
 * followed occasionally; the alternative is restructuring the provider tree for
 * one affordance.
 */
interface ToastAction {
  label: string;
  href: string;
}

interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  action?: ToastAction;
}

interface ToastContextValue {
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id'>) => void;
  removeToast: (id: string) => void;
  success: (title: string, message?: string, action?: ToastAction) => void;
  error: (title: string, message?: string) => void;
  warning: (title: string, message?: string) => void;
  info: (title: string, message?: string) => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

/**
 * Non-throwing toast access (the `useOptionalAuthContext` precedent).
 *
 * For components that *decorate* a surface rather than own it — a header chip,
 * an inline panel — and so get mounted by suites that render the surface
 * without the app shell. A toast is confirmation, never the only channel: the
 * surfaces using this hook all show their outcome on screen as well, so losing
 * the toast outside a provider costs nothing and crashing the host page costs
 * everything.
 */
export function useOptionalToast(): ToastContextValue | undefined {
  return useContext(ToastContext);
}

interface ToastProviderProps {
  children: ReactNode;
}

export function ToastProvider({ children }: ToastProviderProps) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(toast => toast.id !== id));
  }, []);

  const addToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = crypto.randomUUID();
    const newToast: Toast = { ...toast, id };
    setToasts(prev => [...prev, newToast]);

    // `error_shown` (task 427). Toasts are the app's shared error surface, so
    // instrumenting the one factory covers every call site. Only the title is
    // reported, as a slug — the `message` half is where interpolated values
    // (user input, server text) live and must never reach analytics.
    if (toast.type === 'error') {
      trackEvent('error_shown', {
        error_type: toEventSlug(toast.title),
        location: currentLocationTag(),
      });
    }

    // Auto-remove after duration (default 5 seconds)
    const duration = toast.duration ?? 5000;
    if (duration > 0) {
      setTimeout(() => removeToast(id), duration);
    }
  }, [removeToast]);

  const success = useCallback((title: string, message?: string, action?: ToastAction) => {
    addToast({ type: 'success', title, message, action });
  }, [addToast]);

  const error = useCallback((title: string, message?: string) => {
    addToast({ type: 'error', title, message, duration: 8000 }); // Longer for errors
  }, [addToast]);

  const warning = useCallback((title: string, message?: string) => {
    addToast({ type: 'warning', title, message });
  }, [addToast]);

  const info = useCallback((title: string, message?: string) => {
    addToast({ type: 'info', title, message });
  }, [addToast]);

  return (
    <ToastContext.Provider value={{ toasts, addToast, removeToast, success, error, warning, info }}>
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

// Toast Container Component
function ToastContainer({ toasts, onRemove }: { toasts: Toast[]; onRemove: (id: string) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 space-y-2 max-w-sm">
      {toasts.map(toast => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
}

// Individual Toast Component
function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const icons = {
    success: CheckCircleIcon,
    error: XCircleIcon,
    warning: ExclamationCircleIcon,
    info: InformationCircleIcon,
  };

  const colors = {
    success: 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800',
    error: 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800',
    warning: 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800',
    info: 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800',
  };

  const iconColors = {
    success: 'text-green-500',
    error: 'text-red-500',
    warning: 'text-yellow-500',
    info: 'text-blue-500',
  };

  const Icon = icons[toast.type];

  return (
    <div
      className={`flex items-start p-4 rounded-lg border shadow-lg ${colors[toast.type]} animate-slide-in`}
      role="alert"
    >
      <Icon className={`h-5 w-5 ${iconColors[toast.type]} flex-shrink-0`} />
      <div className="ml-3 flex-1">
        <p className="text-sm font-medium text-gray-900 dark:text-white">{toast.title}</p>
        {toast.message && (
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{toast.message}</p>
        )}
        {toast.action && (
          <a
            href={toast.action.href}
            data-testid="toast-action"
            className="mt-1 inline-block text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
          >
            {toast.action.label}
          </a>
        )}
      </div>
      <button
        onClick={() => onRemove(toast.id)}
        className="ml-4 flex-shrink-0 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
      >
        <XMarkIcon className="h-5 w-5" />
      </button>
    </div>
  );
}
